import React, {
  useCallback, useMemo, useState, useEffect,
} from 'react';
import { useSelector } from 'react-redux';
import { DateTime } from 'luxon';
import { useDrag } from 'react-dnd';

import Permissions from '../../auth/Permissions';
import { getIdMap } from '../../helpers/helpers';
import { getBackgroundColor, getStatusIcon } from '../scheduleHelpers';
import { getAllDayValues } from '../../helpers/time';
import MonthShiftHover from '../scheduleHoverCard/MonthShiftHover';

const compactTime = (startTime) => {
  const dt = DateTime.fromMillis(startTime);
  return dt.minute ? dt.toFormat('h:mm a') : dt.toFormat('h a');
};

export default function MonthShiftDayEntry({
  user,
  resourceId,
  shift = {},
  onClick,
  hoverCard = false,
  hoverPlacement = 'bottomLeft',
  userBar = false,
  barStyle,
}) {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'MonthShiftDayEntry',
    item: {
      id: shift?.id, userId: user?.id, resourceId, shift,
    },
    isDragging: (monitor) => shift?.id === monitor.getItem().id,
    collect: (monitor) => ({
      isDragging: shift?.id === monitor?.getItem()?.id,
    }),
  }), [shift, user, resourceId]);

  const {
    startTime,
    endTime,
    title,
    users = [],
    isEvent,
    color,
    isDraft,
    appliedUsers,
    timezone = DateTime.local().zoneName,
  } = shift;
  const [currentUserStatus, setCurrentUserStatus] = useState();

  useEffect(() => {
    if (appliedUsers && appliedUsers.length === 0) return;
    const shiftStatus = appliedUsers?.find((u) => u.userId === Permissions.id)?.status;
    setCurrentUserStatus(shiftStatus);
  }, [shift, appliedUsers]);

  const { isAllDay } = getAllDayValues(
    startTime ? DateTime.fromMillis(startTime, { zone: timezone }) : null,
    endTime ? DateTime.fromMillis(endTime, { zone: timezone }) : null,
  );
  let timeText = '';
  if (startTime && !(userBar && isAllDay)) {
    if (userBar) {
      timeText = compactTime(startTime);
    } else {
      timeText = DateTime.fromMillis(startTime).toLocaleString(DateTime.TIME_SIMPLE);
    }
  }

  const stateUsers = useSelector((state) => state.users.users);

  const handleClick = useCallback((e) => {
    e.stopPropagation();
    onClick(shift);
  }, [shift, onClick]);

  const userMap = useMemo(() => getIdMap(stateUsers), [stateUsers]);
  const isMyShift = useMemo(() => users && users.includes(Permissions.id), [users]);
  const backgroundColor = getBackgroundColor(users, userMap, color, isMyShift, isEvent, isDraft);

  const entry = (
    <div
      className={`schedule-month-day-entry${userBar ? ' schedule-user-shift-bar' : ''}${userBar && isDraft ? ' schedule-user-shift-bar-draft' : ''}`}
      onClick={handleClick}
      ref={drag}
      style={{
        opacity: (isDragging || (isDraft && !userBar)) ? 0.5 : 1,
        ...(userBar ? { '--category-color': backgroundColor } : {}),
        ...barStyle,
      }}
    >
      <div className={`schedule-month-day-entry-dot-${(isMyShift && !isDraft) ? 'mine' : 'other'}`} style={{ backgroundColor }} />
      <span>
        {timeText ? `${timeText} ` : ''}
        <b>{title}</b>
        {isDraft ? getStatusIcon(currentUserStatus) : null}
      </span>
    </div>
  );

  if (!hoverCard) return entry;
  return (
    <MonthShiftHover shift={shift} placement={hoverPlacement}>
      {entry}
    </MonthShiftHover>
  );
}
